/** One-shot Cogni search for the distinct FDA Approval Letters corpus. */
import { useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import { getImpersonationHeaders } from '../services/impersonationHeaders';
import type {
  ApprovalLetterCorpusResult,
  ApprovalLetterCorpusSearchResponse,
} from '../types';

const API_URL =
  import.meta.env['VITE_STREAMING_API_URL'] ||
  import.meta.env['VITE_API_URL'] ||
  'https://us-central1-label-doctor-f255e.cloudfunctions.net/apiProxy';

interface ApprovalLetterSearchState {
  results: ApprovalLetterCorpusResult[];
  isLoading: boolean;
  error: Error | null;
  latencyMs: number | null;
}

export function useApprovalLetterSearch(
  query: string,
  marketingCategories: readonly string[],
  enabled: boolean = true,
  /** 'US_FDA' selects the Drugs@FDA path; omitted -> body identical to before. */
  region?: 'US_FDA',
): ApprovalLetterSearchState {
  const [results, setResults] = useState<ApprovalLetterCorpusResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const categoriesKey = [...new Set(marketingCategories)].sort().join(',');

  useEffect(() => {
    if (!enabled || !query.trim()) {
      setResults([]);
      setIsLoading(false);
      setError(null);
      setLatencyMs(null);
      return;
    }

    const controller = new AbortController();
    const search = async () => {
      setResults([]);
      setIsLoading(true);
      setError(null);
      setLatencyMs(null);

      try {
        const currentUser = auth.currentUser;
        const token = currentUser ? await currentUser.getIdToken(false) : null;
        if (controller.signal.aborted) return;

        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        Object.assign(headers, getImpersonationHeaders());

        const response = await fetch(`${API_URL}/approval-letters/search`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query: query.trim(),
            marketing_categories: categoriesKey ? categoriesKey.split(',') : [],
            ...(region ? { region } : {}),
          }),
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(
            `FDA Approval Letter search failed: ${response.status} ${response.statusText}`,
          );
        }

        const payload = (await response.json()) as ApprovalLetterCorpusSearchResponse;
        if (controller.signal.aborted) return;
        setResults(payload.results);
        setLatencyMs(payload.latency_ms);
        setIsLoading(false);
      } catch (err: unknown) {
        if (
          controller.signal.aborted
          || (err instanceof Error && err.name === 'AbortError')
        ) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsLoading(false);
      }
    };

    void search();
    return () => controller.abort();
  }, [query, categoriesKey, enabled, region]);

  return { results, isLoading, error, latencyMs };
}

export default useApprovalLetterSearch;
